import React, {useState, useEffect, useCallback} from 'react';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {ArrDrawer} from '../../../../../common/arr-drawer/ArrDrawer';
import './ViewDocsFlyout.scss';
import {t} from 'i18next';
import ViewDocsDataGrid from './view-docs-data-grid/ViewDocsDataGrid';
import {DataGridRowSelectionModel} from '../../../../../../shared/interfaces';
import {useDispatch, useSelector} from 'react-redux';
import {ReactComponent as InfoIcon} from '../../../../../../assets/images/info-icon.svg';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../../../redux/slice/docListSlice';
import {
  BgNavigationTypes,
  SourceTypeCode,
} from '../../../../../../shared/constants';
import {RootState} from '../../../../../../redux/store';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {clearDocViewState} from '../../../../../document-viewer/docViewerUtils';
import {IInvestigateNavigationMasterListPayload} from '../../../../../../api/types/aiInvestigate.type';
import {useCreateInvestigateNavigationMasterList} from '../../../../../../hooks/ai/investigate/useInvestigate';

export interface ViewDocsFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
  size?: 'large';
  conversationNumber: number;
  transcriptId: number | string;
  indexId: number;
  conversationId: number | string;
}

const ViewDocsFlyout: React.FC<ViewDocsFlyoutProps> = ({
  isOpen = false,
  onClose,
  loading: _loading = false,
  size = 'large',
  conversationNumber,
  transcriptId,
  indexId,
  conversationId,
}: ViewDocsFlyoutProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [unselectedIds, setUnselectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [totalGridCount, setTotalGridCount] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const docListMasterId = useSelector(
    (state: RootState) => state.docList.docListId
  );

  const [enableViewInDocListButton, setEnableViewInDocListButton] =
    useState(false);

  const {mutate: createMasterList} = useCreateInvestigateNavigationMasterList(
    (response) => {
      clearDocViewState();
      dispatch(setDocListId(Number(response.doclistMasterId)));
      setIsNavigating(true);
    }
  );

  useEffect(() => {
    if (!isOpen) return;
  }, [isOpen]);

  useEffect(() => {
    if (!isNavigating) return;

    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        navType === SourceTypeCode.DOCLIST
      ) {
        setIsNavigating(false);
        setIsLoading(false);
        const backPath = `${window.location.pathname}${window.location.search}`;
        dispatch(
          setDocListBackNavigation({
            label: t('ai.investigate.aiInvestigate'),
            path: backPath,
          })
        );
        const newWindow = window.open('about:blank', '_blank');
        if (newWindow) {
          newWindow.location.href = '/Document/DocList';
        }
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsNavigating(false);
      setIsLoading(false);
    }
  }, [dispatch, docListMasterId, isNavigating, lastEvent]);

  const handleClose = () => {
    onClose();
  };

  const handleViewDocsSelection = (data: DataGridRowSelectionModel) => {
    setSelectedIds(data.selectedIds);
    setUnselectedIds(data.unselectedIds);
    setIsAllSelected(data.isAllSelected);
    setTotalGridCount(data.totalCount ?? 0);
    setEnableViewInDocListButton(
      !!data.totalCount && (data.isAllSelected || data.selectedIds.length > 0)
    );
  };

  const handleViewInDocList = useCallback(() => {
    const selectedConversationID = Number(conversationId);
    const payload: IInvestigateNavigationMasterListPayload = {
      destinationTypeCode: SourceTypeCode.DOCLIST,
      isInverse: isAllSelected,
      selectedDocIDs: isAllSelected ? '' : selectedIds.join(','),
      excludedDocIDs: isAllSelected ? unselectedIds.join(',') : '',
      selectedConversationID: Number.isFinite(selectedConversationID)
        ? selectedConversationID
        : 0,
      documentCount: isAllSelected
        ? Math.max(totalGridCount - unselectedIds.length, 0)
        : selectedIds.length,
    };

    setIsLoading(true);
    createMasterList(payload);
  }, [
    conversationId,
    createMasterList,
    isAllSelected,
    selectedIds,
    totalGridCount,
    unselectedIds,
  ]);

  return (
    <ArrDrawer
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      title=""
      side="right"
      showButton={false}
      showHeader={false}
      className="ai-chat-panel-view-docs-flyout"
      ariaLabel="View Docs Flyout"
    >
      {isLoading && <ArrLoadingSpinner overlay isOpen={isLoading} />}
      <Box
        className="ai-chat-panel-view-docs-flyout__header"
        data-testid="ai-chat-panel-view-docs-header"
      >
        <ArrButton
          variant="secondary"
          size="medium"
          onClick={handleClose}
          dataTestId="ai-chat-panel-view-docs-close-button"
          aria-label="Close View Docs Panel"
        >
          {t('common.close')}
        </ArrButton>
      </Box>
      <Box className="ai-chat-panel-view-docs-flyout__headers">
        <Box className="title-box">
          <Typography
            component="h3"
            id="view-docs-title"
            className="typo-text headline3_size title"
            data-testid="flyout-title"
          >
            {`Q${conversationNumber}`} {t('ai.investigate.relatedDocument')}
          </Typography>
        </Box>
        <Box className="action-buttons-box">
          <ArrButton
            variant="primary"
            size="medium"
            onClick={handleViewInDocList}
            disabled={!enableViewInDocListButton}
            aria-label={t('ai.investigate.viewInDocList')}
            dataTestId="doclist-view"
          >
            {t('ai.investigate.viewInDocList')}
          </ArrButton>
        </Box>
      </Box>
      <Box>
        <ViewDocsDataGrid
          transcriptId={transcriptId}
          indexId={indexId}
          conversationId={conversationId}
          selectedIds={selectedIds}
          unselectedIds={unselectedIds}
          isAllSelected={isAllSelected}
          onSelectionStateChange={handleViewDocsSelection}
        />
        <Box
          className="ai-chat-panel-view-docs-flyout__disclaimer"
          data-testid="ai-chat-panel-view-docs-flyout-disclaimer"
        >
          <InfoIcon
            className="ai-chat-panel-view-docs-flyout__disclaimer-icon"
            aria-hidden="true"
            focusable="false"
            data-testid="ai-chat-panel-view-docs-flyout-disclaimer-icon"
          />
          <Typography
            className="ai-chat-panel-view-docs-flyout__disclaimer-text"
            data-testid="ai-chat-panel-view-docs-flyout-disclaimer-text"
          >
            {t('ai.investigate.disclaimer')}{' '}
            <Typography
              component="span"
              className="ai-chat-panel-view-docs-flyout__disclaimer_details-text"
              data-testid="ai-chat-panel-view-docs-flyout-disclaimer-details-text"
            >
              {t('ai.investigate.viewDocsDisclaimer')}
            </Typography>
          </Typography>
        </Box>
      </Box>
    </ArrDrawer>
  );
};

export default ViewDocsFlyout;
